import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Check, X, Sparkles, Zap, Crown, Building2, 
  CreditCard, Shield, Clock, Users, TrendingUp,
  ArrowRight, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const Pricing = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);
  const [isAnnual, setIsAnnual] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      id: 'free',
      name: 'Gratuito',
      description: 'Para empezar a controlar tus finanzas',
      priceMonthly: 0,
      priceAnnual: 0,
      icon: Zap,
      color: 'text-gray-500',
      bgColor: 'bg-gray-50 dark:bg-gray-900',
      borderColor: 'border-gray-200 dark:border-gray-800',
      features: [
        { text: '50 transacciones/mes', included: true },
        { text: '2 cuentas bancarias', included: true },
        { text: '3 presupuestos', included: true },
        { text: 'Categorías ilimitadas', included: true },
        { text: 'Asistente IA (10 consultas/mes)', included: true },
        { text: 'Escaneo recibos (5/mes)', included: true },
        { text: 'Exportar informes', included: false },
        { text: 'Modo Familia/Pareja', included: false },
        { text: 'Inversiones tracking', included: false },
        { text: 'Open Banking', included: false },
        { text: 'Soporte prioritario', included: false },
      ],
      cta: 'Plan Actual',
      disabled: user?.plan === 'free',
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'Control total de tus finanzas personales',
      priceMonthly: 9.99,
      priceAnnual: 7.99,
      icon: Crown,
      color: 'text-amber-500',
      bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30',
      borderColor: 'border-amber-300 dark:border-amber-700',
      popular: true,
      features: [
        { text: 'Transacciones ilimitadas', included: true },
        { text: 'Cuentas ilimitadas', included: true },
        { text: 'Presupuestos ilimitados', included: true },
        { text: 'Categorías ilimitadas', included: true },
        { text: 'Asistente IA ilimitado', included: true },
        { text: 'Escaneo recibos ilimitado', included: true },
        { text: 'Exportar informes PDF/Excel', included: true },
        { text: 'Modo Familia/Pareja', included: true },
        { text: 'Inversiones tracking', included: true },
        { text: 'Open Banking', included: false },
        { text: 'Soporte prioritario', included: true },
      ],
      cta: user?.plan === 'premium' ? 'Plan Actual' : 'Comenzar Premium',
      disabled: user?.plan === 'premium',
    },
    {
      id: 'business',
      name: 'Business',
      description: 'Para autónomos y pequeños negocios',
      priceMonthly: 24.99,
      priceAnnual: 19.99,
      icon: Building2,
      color: 'text-emerald-600',
      bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30',
      borderColor: 'border-emerald-300 dark:border-emerald-700',
      features: [
        { text: 'Todo de Premium', included: true },
        { text: 'Multi-empresa', included: true },
        { text: 'Facturación integrada', included: true },
        { text: 'Open Banking conexión', included: true },
        { text: 'Importación bancaria auto', included: true },
        { text: 'Informes fiscales', included: true },
        { text: 'API acceso', included: true },
        { text: 'Usuarios equipo (hasta 5)', included: true },
        { text: 'Auditoría completa', included: true },
        { text: 'Soporte dedicado 24/7', included: true },
        { text: 'Onboarding personalizado', included: true },
      ],
      cta: 'Contactar Ventas',
      disabled: false,
    },
  ];

  const handleSelectPlan = async (plan) => {
    if (plan.disabled) return;
    
    setSelectedPlan(plan.id);
    setLoading(true);

    // TODO: Integrar con Stripe Checkout
    // Esta función está preparada para conectar con Stripe
    try {
      if (plan.id === 'business') {
        // Para Business, abrir formulario de contacto
        toast.info('Próximamente: Nuestro equipo te contactará para configurar tu plan Business.');
        // window.open('mailto:ventas@trebolfinance.com?subject=Plan Business', '_blank');
      } else if (plan.id === 'premium') {
        // Para Premium, preparado para Stripe Checkout
        toast.info('Próximamente: Pasarela de pago con Stripe. Contacta con soporte para activar Premium.');
        
        // Código preparado para Stripe:
        // const response = await fetch('/api/payments/create-checkout-session', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        //   body: JSON.stringify({ 
        //     plan: plan.id, 
        //     billing: isAnnual ? 'annual' : 'monthly',
        //     priceId: isAnnual ? 'price_annual_xxx' : 'price_monthly_xxx'
        //   })
        // });
        // const { url } = await response.json();
        // window.location.href = url; // Redirect to Stripe Checkout
      }
    } catch (error) {
      toast.error('Error al procesar la solicitud');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Gratis';
    return `€${price.toFixed(2)}`;
  };

  return (
    <div className="space-y-8 pb-8" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center space-y-4">
        <Badge variant="outline" className="text-primary border-primary">
          <Sparkles size={14} className="mr-1" />
          Planes y Precios
        </Badge>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground">
          Elige el plan perfecto para ti
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Desde control básico hasta gestión empresarial completa. 
          Todos los planes incluyen actualizaciones gratuitas.
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 pt-4">
          <span className={cn(
            "text-sm font-medium transition-colors",
            !isAnnual ? "text-foreground" : "text-muted-foreground"
          )}>
            Mensual
          </span>
          <Switch 
            checked={isAnnual} 
            onCheckedChange={setIsAnnual}
            data-testid="billing-toggle"
          />
          <span className={cn(
            "text-sm font-medium transition-colors flex items-center gap-2",
            isAnnual ? "text-foreground" : "text-muted-foreground"
          )}>
            Anual
            <Badge className="bg-emerald-500 text-white text-xs">
              -20%
            </Badge>
          </span>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {plans.map((plan) => {
          const Icon = plan.icon;
          const price = isAnnual ? plan.priceAnnual : plan.priceMonthly;
          const isCurrentPlan = user?.plan === plan.id;

          return (
            <Card 
              key={plan.id}
              className={cn(
                "relative overflow-hidden transition-all duration-300 hover:shadow-lg",
                plan.bgColor,
                plan.borderColor,
                plan.popular && "ring-2 ring-amber-400 dark:ring-amber-500",
                isCurrentPlan && "ring-2 ring-primary"
              )}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0">
                  <Badge className="rounded-none rounded-bl-lg bg-amber-500 text-white">
                    <Star size={12} className="mr-1" fill="currentColor" />
                    Popular
                  </Badge>
                </div>
              )}

              {isCurrentPlan && (
                <div className="absolute top-0 left-0">
                  <Badge className="rounded-none rounded-br-lg bg-primary text-primary-foreground">
                    <Check size={12} className="mr-1" />
                    Tu Plan
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-2">
                <div className={cn(
                  "w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center",
                  plan.id === 'free' && "bg-gray-200 dark:bg-gray-800",
                  plan.id === 'premium' && "bg-amber-100 dark:bg-amber-900/50",
                  plan.id === 'business' && "bg-emerald-100 dark:bg-emerald-900/50"
                )}>
                  <Icon className={cn("w-7 h-7", plan.color)} />
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>

              <CardContent className="text-center pb-4">
                <div className="mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-foreground">
                    {formatPrice(price)}
                  </span>
                  {price > 0 && (
                    <span className="text-muted-foreground text-sm">/mes</span>
                  )}
                  {isAnnual && price > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Facturado anualmente (€{(price * 12).toFixed(2)}/año)
                    </p>
                  )}
                </div>

                <ul className="space-y-3 text-left">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      {feature.included ? (
                        <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <X size={16} className="text-gray-300 dark:text-gray-600 mt-0.5 flex-shrink-0" />
                      )}
                      <span className={cn(
                        feature.included ? "text-foreground" : "text-muted-foreground"
                      )}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button 
                  className={cn(
                    "w-full",
                    plan.popular && !plan.disabled && "bg-amber-500 hover:bg-amber-600 text-white",
                    plan.id === 'business' && !plan.disabled && "bg-emerald-600 hover:bg-emerald-700 text-white"
                  )}
                  variant={plan.disabled ? "outline" : "default"}
                  disabled={plan.disabled || loading}
                  onClick={() => handleSelectPlan(plan)}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {loading && selectedPlan === plan.id ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                      Procesando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {plan.cta}
                      {!plan.disabled && <ArrowRight size={16} />}
                    </span>
                  )}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Features Comparison */}
      <Card className="max-w-4xl mx-auto mt-12">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="text-primary" />
            Garantías incluidas en todos los planes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Datos seguros</h4>
                <p className="text-xs text-muted-foreground">Cifrado end-to-end</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Cancela cuando quieras</h4>
                <p className="text-xs text-muted-foreground">Sin permanencia</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CreditCard size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Pago seguro</h4>
                <p className="text-xs text-muted-foreground">Powered by Stripe</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <TrendingUp size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Actualizaciones</h4>
                <p className="text-xs text-muted-foreground">Nuevas funciones gratis</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQ or Contact */}
      <div className="text-center pt-8">
        <p className="text-muted-foreground">
          ¿Tienes dudas? Escríbenos a{' '}
          <a href="mailto:soporte@trebolfinance.com" className="text-primary hover:underline">
            soporte@trebolfinance.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default Pricing;
